import { BotSequenceType } from "@swivl-mono/types";

/** URL slug ↔ sequence type. The page iterates `BotSequenceType`, so a new type only needs an entry here. */
export interface SequenceTypeMeta {
  type: BotSequenceType;
  slug: string;
  label: string;
  /** One line under the type heading. */
  description: string;
  /** What Day 0 is anchored to, for the day captions. */
  anchorLabel: string;
  /** Whether "Send immediately" is offered on this type's days (ENG-3216 decision 8). */
  offersSendImmediately: boolean;
}

export const SEQUENCE_TYPE_META: Record<BotSequenceType, SequenceTypeMeta> = {
  [BotSequenceType.PAST_DUE]: {
    type: BotSequenceType.PAST_DUE,
    slug: "past-due",
    label: "Past Due",
    description: "Reminders to tenants whose rent is past due. Day 0 is the due date.",
    anchorLabel: "the due date",
    offersSendImmediately: false
  },
  [BotSequenceType.MOVE_IN]: {
    type: BotSequenceType.MOVE_IN,
    slug: "move-in",
    label: "Move In",
    description: "Messages around a tenant's move-in. Day 0 is move-in day.",
    anchorLabel: "move-in",
    offersSendImmediately: true
  },
  [BotSequenceType.MOVE_OUT]: {
    type: BotSequenceType.MOVE_OUT,
    slug: "move-out",
    label: "Move Out",
    description: "Messages after a tenant moves out. Day 0 is move-out day.",
    anchorLabel: "move-out",
    offersSendImmediately: true
  },
  [BotSequenceType.ABANDONED_CART]: {
    type: BotSequenceType.ABANDONED_CART,
    slug: "abandoned-cart",
    label: "Abandoned Cart",
    description: "Follow-ups to leads who started renting online and stopped. Day 0 is the day the cart was started.",
    anchorLabel: "the cart was started",
    offersSendImmediately: false
  }
};

/** Display order in the left column. */
export const SEQUENCE_TYPE_ORDER: BotSequenceType[] = [
  BotSequenceType.PAST_DUE,
  BotSequenceType.MOVE_IN,
  BotSequenceType.MOVE_OUT,
  BotSequenceType.ABANDONED_CART
];

export function sequenceTypeForSlug(slug: string | undefined): BotSequenceType | null {
  const match = Object.values(SEQUENCE_TYPE_META).find((meta) => meta.slug === slug);
  return match ? match.type : null;
}

/** "On the due date" / "3 days after the due date" — offsets are always on or AFTER the anchor. */
export function dayCaption(type: BotSequenceType, offsetDays: number): string {
  const meta = SEQUENCE_TYPE_META[type];
  if (offsetDays === 0) {
    return type === BotSequenceType.MOVE_IN || type === BotSequenceType.MOVE_OUT ? `On ${meta.anchorLabel} day` : `On ${meta.anchorLabel}`;
  }
  const unit = offsetDays === 1 ? "day" : "days";
  return `${offsetDays} ${unit} after ${meta.anchorLabel}`;
}
